import { useEffect, useState } from "react";
import { API, useCurrency } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Search, SlidersHorizontal, CreditCard, CheckCircle, Clock, XCircle, X } from "lucide-react";

export default function AdminWalletRecharges() {
  const { symbol } = useCurrency();
  const [recharges, setRecharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    dateFrom: "",
    dateTo: "",
  });

  useEffect(() => {
    fetchRecharges();
  }, []);

  const fetchRecharges = async () => {
    try {
      const response = await fetch(`${API}/admin/recharges`, { credentials: "include" });
      if (response.ok) setRecharges(await response.json());
    } catch (error) {
      console.error("Failed to fetch recharges:", error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = recharges.filter((r) => {
    if (filters.search) {
      const q = filters.search.toLowerCase();
      if (!(r.user_email || "").toLowerCase().includes(q) && !(r.user_name || "").toLowerCase().includes(q) && !(r.transaction_id || "").toLowerCase().includes(q)) return false;
    }
    if (filters.status !== "all" && r.payment_status !== filters.status) return false;
    if (filters.dateFrom) {
      if (new Date(r.created_at) < new Date(filters.dateFrom)) return false;
    }
    if (filters.dateTo) {
      const to = new Date(filters.dateTo);
      to.setHours(23, 59, 59, 999);
      if (new Date(r.created_at) > to) return false;
    }
    return true;
  });

  const totalPaid = filtered.filter((r) => r.payment_status === "paid").reduce((s, r) => s + (r.amount || 0), 0);
  const totalCredits = filtered.filter((r) => r.payment_status === "paid").reduce((s, r) => s + (r.credits || 0), 0);

  const clearFilters = () => setFilters({ search: "", status: "all", dateFrom: "", dateTo: "" });
  const activeFilterCount = Object.entries(filters).filter(([k, v]) => k === "status" ? v !== "all" : v !== "").length;

  const statusIcon = (status) => {
    if (status === "paid") return <CheckCircle size={14} className="text-green-500" />;
    if (status === "pending") return <Clock size={14} className="text-yellow-500" />;
    return <XCircle size={14} className="text-red-500" />;
  };

  const statusColor = (status) => {
    if (status === "paid") return "bg-green-100 text-green-700";
    if (status === "pending") return "bg-yellow-100 text-yellow-700";
    return "bg-red-100 text-red-700";
  };

  const formatDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-recharges">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Wallet Recharges</h1>
          <div className="flex gap-4 text-sm">
            <div className="bg-green-50 px-3 py-1.5 rounded-lg">
              <span className="text-green-600 font-semibold">{symbol}{Math.round(totalPaid)}</span>
              <span className="text-green-500 ml-1">collected</span>
            </div>
            <div className="bg-blue-50 px-3 py-1.5 rounded-lg">
              <span className="text-blue-600 font-semibold">{symbol}{Math.round(totalCredits)}</span>
              <span className="text-blue-500 ml-1">credits issued</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 space-y-3" data-testid="recharges-filter-bar">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input placeholder="Search by name, email, transaction ID..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="pl-11 bg-white" data-testid="recharges-search" />
            </div>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="w-36" data-testid="recharges-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className={`gap-2 ${activeFilterCount > 1 ? "border-green-500 text-green-600" : ""}`} data-testid="recharges-filter-toggle">
              <SlidersHorizontal size={16} /> More
            </Button>
            {activeFilterCount > 0 && <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500"><X size={14} className="mr-1" /> Clear</Button>}
          </div>
          {showFilters && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-2 gap-3 animate-fade-in">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Date From</Label>
                <Input type="date" value={filters.dateFrom} onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })} className="bg-white h-9 text-sm" data-testid="recharges-date-from" />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Date To</Label>
                <Input type="date" value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} className="bg-white h-9 text-sm" data-testid="recharges-date-to" />
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="skeleton h-16 w-full rounded-xl" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
            <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No recharges found</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left p-4 text-gray-500 font-medium text-sm">Customer</th>
                    <th className="text-left p-4 text-gray-500 font-medium text-sm">Plan</th>
                    <th className="text-left p-4 text-gray-500 font-medium text-sm">Amount</th>
                    <th className="text-left p-4 text-gray-500 font-medium text-sm">Credits</th>
                    <th className="text-left p-4 text-gray-500 font-medium text-sm">Status</th>
                    <th className="text-left p-4 text-gray-500 font-medium text-sm">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((r) => (
                    <tr key={r.transaction_id} className="hover:bg-gray-50" data-testid={`recharge-${r.transaction_id}`}>
                      <td className="p-4">
                        <p className="font-medium text-gray-900">{r.user_name || r.user_email}</p>
                        <p className="text-[10px] text-gray-500">{r.user_email}</p>
                        {r.user_phone && <p className="text-[10px] text-gray-500">{r.user_phone}</p>}
                      </td>
                      <td className="p-4 text-gray-600 text-sm">{r.plan_name || "-"}</td>
                      <td className="p-4 font-medium text-gray-900">{symbol}{Math.round(r.amount || 0)}</td>
                      <td className="p-4 font-semibold text-green-600">{symbol}{Math.round(r.credits || 0)}</td>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusColor(r.payment_status)}`}>
                          {statusIcon(r.payment_status)}
                          {r.payment_status}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-gray-500">{formatDate(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
